import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computed } from 'mobx';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { openInClientStore } from 'owa-addins-core';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import {
    getDefaultSlrAppRibbonButtonStyles,
    getSlrHeroAppSplitButtonStyles,
} from 'owa-command-ribbon-styles';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getMailMenuItemShouldShowMap } from 'owa-mail-filterable-menu-behavior';
import { MenuItemType } from 'owa-filterable-menu';
import { ControlInfo, getControlInfo } from 'owa-mail-ribbon-utils';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import { ActionControlId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import { getPalette } from 'owa-theme';
import { isYammerPublisherItemEnabled } from '../../../../util/isYammerPublisherItemEnabled';
import { lazyTryShowYammerPostTeachingMoment } from 'owa-yammer-post';
import {
    onArchiveClicked,
    onDeleteClicked,
    onNewEmailSplitButtonExecuted,
    onNewMessage,
    onNewNoteClicked,
    onPinUnpinClicked,
    onRibbonCustomizerClicked,
    onSweepClicked,
    onToggleFolderPane,
    onUndoClicked,
} from '../../../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppSplitButton,
} from '../../../../util/createMailRibbonButton';
import { openDiscoverGroups } from '../../../../util/onGroupsCommandActions';
import {
    getCategorizeSections,
    getDeleteSections,
    getMoveToSections,
    getNewGroupSections,
    getSnoozeSections,
    resetCategorizeSearch,
    resetMoveToSearch,
} from '../../../sections';

/**
 * This file contains the definitions for all the controls
 * in the Single Line Ribbon (SLR)
 */

// Placeholder for required onExecute parameter, being used temporarily
// until stubbed out ribbon buttons have been hooked up.
const unhandledOnExecute = () => {};

const computeLeftToggleControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ToggleLeftPane);
        return createMailAppButton(
            MailRibbonControlId.ToggleLeftPane,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onToggleFolderPane,
            undefined /*keytipProps*/,
            controlInfo.iconName
        );
    }
);
export const getLeftToggleControl = () => computeLeftToggleControl.get();

export const getNewMessageControl = computedFn(
    (keytipTabId: MailRibbonTabId): RibbonControlProps => {
        const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();
        const isNote = shouldShowBehaviorMap[MenuItemType.Ribbon_NewNote]();
        const controlInfo = getControlInfo(MailRibbonControlId.NewMessageSplitButton);

        if (isYammerPublisherItemEnabled()) {
            lazyTryShowYammerPostTeachingMoment.importAndExecute(
                `[data-unique-id='Ribbon-${MailRibbonControlId.NewMessageSplitButton}']`
            );
        }
        return createMailAppSplitButton(
            MailRibbonControlId.NewMessageSplitButton,
            controlInfo.text,
            controlInfo.ariaText,
            isNote ? undefined : controlInfo.tooltip,
            false /*isPopout*/,
            {
                sections: [
                    {
                        controls: getNewGroupSections(keytipTabId),
                    },
                ],
            },
            isNote ? onNewNoteClicked : onNewMessage,
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getPalette().white,
            getSlrHeroAppSplitButtonStyles(
                false /*isToolbarComposeStyle*/,
                undefined /*rightMarginConstants*/
            ),
            {
                onMenuButtonExecute: onNewEmailSplitButtonExecuted,
                primaryDisabled:
                    !shouldShowBehaviorMap[MenuItemType.Ribbon_NewMessage]() &&
                    !shouldShowBehaviorMap[MenuItemType.Ribbon_NewNote](),
            }
        );
    }
);

export const getDeleteControl = computedFn(
    (targetWindow: Window): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.DeleteItem);
        return createMailAppSplitButton(
            MailRibbonControlId.DeleteItem,
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            false /*isPopout*/,
            { sections: getDeleteSections(undefined /* itemId */, targetWindow) },
            onDeleteClicked(undefined /* itemId */, targetWindow),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.DeleteItem),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

const computeArchiveControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Archive);
        return createMailAppButton(
            MailRibbonControlId.Archive,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onArchiveClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.Archive)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getArchiveControl = () => computeArchiveControl.get();

const computeSweepControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Sweep);
        return createMailAppButton(
            MailRibbonControlId.Sweep,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onSweepClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.Sweep)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getSweepControl = () => computeSweepControl.get();

function getMoveControlIconColor(): string | undefined {
    return !isFeatureEnabled('mon-ribbon-toolbar-color') &&
        getIsViewModeSelected(CommandingViewMode.CommandBar)
        ? getPalette().black
        : undefined;
}

export const getMoveControl = computedFn(
    (keytipTabId: MailRibbonTabId | undefined): RibbonControlProps => {
        const controlInfo: ControlInfo = getControlInfo(MailRibbonControlId.Move);

        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Move,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            {
                sections: getMoveToSections(keytipTabId),
            },
            constructKeytip(
                keytipTabId ? [getKeytipString(keytipTabId)] : [],
                getKeytipString(MailRibbonControlId.Move),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getMoveControlIconColor(),
            getDefaultSlrAppRibbonButtonStyles(),
            {
                delayUpdateFocusOnHover: true,
                onAfterMenuDismiss: () => {
                    resetMoveToSearch(false /*onEnter*/);
                },
                onExecute: () => {
                    resetMoveToSearch(true /*onEnter*/);
                },
            }
        );
    }
);

export const computeCategorizeControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Categorize);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Categorize,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            { sections: getCategorizeSections(MailRibbonTabId.Tab_Home) },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.Categorize),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles(),
            {
                delayUpdateFocusOnHover: true,
                onAfterMenuDismiss: () => {
                    resetCategorizeSearch();
                },
            }
        );
    }
);
export const getCategorizeControl = () => computeCategorizeControl.get();

const computePinUnpinControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.PinUnpin);
        return createMailAppButton(
            MailRibbonControlId.PinUnpin,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onPinUnpinClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.PinUnpin)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getPinUnpinControl = () => computePinUnpinControl.get();

const computeSnoozeControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Snooze);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Snooze,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            { sections: getSnoozeSections() },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.Snooze),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getSnoozeControl = () => computeSnoozeControl.get();

const computeAddressBookControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.AddressBook);
        return createMailAppButton(
            MailRibbonControlId.AddressBook,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            unhandledOnExecute,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.AddressBook)
            ),
            controlInfo.iconName
        );
    }
);
export const getAddressBookControl = () => computeAddressBookControl.get();

const computeDiscoverGroupsControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.BrowseGroups);
        return createMailAppButton(
            MailRibbonControlId.BrowseGroups,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            openDiscoverGroups,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.BrowseGroups)
            ),
            controlInfo.iconName
        );
    }
);
export const getDiscoverGroupsControl = () => computeDiscoverGroupsControl.get();

const computeUndoControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Undo);
        return createMailAppButton(
            MailRibbonControlId.Undo,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onUndoClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.Undo)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getUndoControl = () => computeUndoControl.get();

const computeAddInsControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(ActionControlId.GetAddIns);
        return createMailAppButton(
            ActionControlId.GetAddIns,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            openInClientStore,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(ActionControlId.GetAddIns)
            ),
            controlInfo.iconName
        );
    }
);
export const getAddInsControl = () => computeAddInsControl.get();

const computeRibbonCustomizerControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RibbonCustomizer);
        return createMailAppButton(
            MailRibbonControlId.RibbonCustomizer,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            () => {
                onRibbonCustomizerClicked();
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.RibbonCustomizer)
            ),
            undefined /* icon */,
            undefined /* iconColor */,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getRibbonCustomizerControl = () => computeRibbonCustomizerControl.get();
